import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AdminRole, isAdminRole, hasPermission, AdminSection } from '@/lib/permissions';

interface AuthEvent {
  event: string;
  timestamp: number;
  userId?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AdminRole | null;
  isAdmin: boolean;
  loading: boolean;
  lastAuthEvent: AuthEvent | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  hasAccess: (section: AdminSection) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastAuthEvent, setLastAuthEvent] = useState<AuthEvent | null>(null);

  // Prevent overlapping role checks (can happen with rapid auth events)
  const roleCheckInFlightRef = useRef<Promise<void> | null>(null);
  const lastRoleCheckUserIdRef = useRef<string | null>(null);

  const debug = (...args: any[]) => {
    if (import.meta.env.DEV) console.debug('[Auth]', ...args);
  };

  const withTimeout = async <T,>(promise: Promise<T>, ms = 8000): Promise<T> => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error('timeout')), ms);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  };

  const checkUserRole = useCallback(async (userId: string): Promise<void> => {
    // Dedupe: if we already checked for this user and a check is in-flight, await it
    if (roleCheckInFlightRef.current && lastRoleCheckUserIdRef.current === userId) {
      await roleCheckInFlightRef.current;
      return;
    }

    lastRoleCheckUserIdRef.current = userId;

    const task = (async () => {
      debug('checkUserRole:start', { userId });

      try {
        type RoleQueryResult = { data: { role: AdminRole } | null; error: { message?: string } | null };

        // Supabase query builders are thenables but not always typed as full Promises.
        // Wrap into a real Promise so Promise.race/timeout works reliably.
        const queryThenable = supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .single();

        const queryPromise: Promise<RoleQueryResult> = new Promise((resolve, reject) => {
          (queryThenable as any).then(resolve, reject);
        });

        const { data, error } = await withTimeout<RoleQueryResult>(queryPromise, 8000);

        if (error || !data) {
          debug('checkUserRole:missing', { error: error?.message });
          setRole(null);
          setIsAdmin(false);
          return;
        }

        const userRole = data.role as AdminRole;
        debug('checkUserRole:ok', { userRole });
        setRole(userRole);
        setIsAdmin(isAdminRole(userRole));
      } catch (error) {
        debug('checkUserRole:error', error);
        console.error('Error checking user role:', error);
        setRole(null);
        setIsAdmin(false);
      } finally {
        debug('checkUserRole:end');
      }
    })();

    roleCheckInFlightRef.current = task;
    try {
      await task;
    } finally {
      if (roleCheckInFlightRef.current === task) {
        roleCheckInFlightRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      debug('initializeAuth:start');
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        debug('initializeAuth:session', { hasSession: !!session, userId: session?.user?.id });
        
        if (session?.user) {
          await checkUserRole(session.user.id);
        } else {
          setRole(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        if (mounted) {
          debug('initializeAuth:end -> loading:false');
          setLoading(false);
        }
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        debug('onAuthStateChange', { event, userId: session?.user?.id });

        // Track auth event for debugging UI
        setLastAuthEvent({
          event,
          timestamp: Date.now(),
          userId: session?.user?.id,
        });

        // Ensure route guards wait until role is resolved for this auth event
        setLoading(true);
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await checkUserRole(session.user.id);
        } else {
          setRole(null);
          setIsAdmin(false);
        }

        if (mounted) {
          debug('onAuthStateChange:end -> loading:false');
          setLoading(false);
        }
      }
    );

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [checkUserRole]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setRole(null);
    setIsAdmin(false);
  };

  const hasAccess = (section: AdminSection): boolean => {
    return hasPermission(role, section);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      role, 
      isAdmin, 
      loading, 
      lastAuthEvent,
      signIn, 
      signOut,
      hasAccess 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
